import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAllGyms } from '../../services/firebase/gym';
import { Gym } from '../../models/gym/Gym';
import HamburgerMenu from '../../components/HamburgerMenu';
import '../../styles/MyGyms.css';

const MyGymsList: React.FC = () => {
  const [myGyms, setMyGyms] = useState<Gym[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { currentUser, climberProfile } = useAuth();

  useEffect(() => {
    const fetchMyGyms = async () => {
      if (!currentUser || !climberProfile) {
        setError(currentUser ? 'Loading user profile...' : 'User not logged in');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Get user's gym memberships where they have owner or fre role
        // Use climber profile from context
        if (!climberProfile.gymMemberships) {
          setMyGyms([]);
          setLoading(false);
          return;
        }
        
        // Filter gym IDs where user has owner or fre role
        const ownerOrFreGymIds = Object.entries(climberProfile.gymMemberships)
          .filter(([_, role]) => role === 'owner' || role === 'fre')
          .map(([gymId, _]) => gymId);
        
        if (ownerOrFreGymIds.length === 0) {
          setMyGyms([]);
          setLoading(false);
          return;
        }
        
        // Fetch all gyms
        const allGyms = await getAllGyms();
        
        // Filter to include only the user's gyms where they have owner/fre role
        const filteredGyms = allGyms.filter(gym => 
          ownerOrFreGymIds.includes(gym.id)
        );
        
        setMyGyms(filteredGyms);
        
        // If only one gym, navigate directly to the detail page
        if (filteredGyms.length === 1) {
          navigate(`/my-gyms/${filteredGyms[0].id}/edit`);
          return;
        }
        
      } catch (err: any) {
        console.error('Error fetching gyms:', err);
        setError('Failed to load your gyms. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMyGyms();
  }, [currentUser, climberProfile, navigate]);

  const handleGymClick = (gymId: string) => {
    navigate(`/my-gyms/${gymId}/edit`);
  };

  if (loading) {
    return (
      <div className="gyms-container">
        <div className="gyms-header fixed-header">
          <h1>My Gyms</h1>
          <div className="header-actions">
            <HamburgerMenu />
          </div>
        </div>
        <div className="header-spacer"></div>
        <div className="loading-indicator">Loading gyms...</div>
      </div>
    );
  }

  if (myGyms.length === 0 && !loading) {
    return (
      <div className="gyms-container">
        <div className="gyms-header fixed-header">
          <h1>My Gyms</h1>
          <div className="header-actions">
            <HamburgerMenu />
          </div>
        </div>
        <div className="header-spacer"></div>
        
        <div className="empty-gyms">
          <p>You are not an owner or administrator of any gyms.</p>
          <button 
            className="back-button"
            onClick={() => navigate('/gyms')}
          >
            View All Gyms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gyms-container">
      <div className="gyms-header fixed-header">
        <h1>My Gyms</h1>
        <div className="header-actions">
          <HamburgerMenu />
        </div>
      </div>
      <div className="header-spacer"></div>

      {error && <div className="error-message">{error}</div>}

      <div className="gyms-list">
        {myGyms.map(gym => (
          <div 
            key={gym.id} 
            className="gym-card"
            onClick={() => handleGymClick(gym.id)}
          >
            <div className="gym-image-container">
              {gym.imageUrl ? (
                <img 
                  src={gym.imageUrl} 
                  alt={gym.name || 'Gym'} 
                  className="gym-image" 
                />
              ) : (
                <div className="gym-image-placeholder">
                  {gym.logoUrl ? (
                    <img 
                      src={gym.logoUrl} 
                      alt={`${gym.name} logo`} 
                      className="gym-logo" 
                    />
                  ) : (
                    <div className="gym-icon">🧗</div>
                  )}
                </div>
              )}
            </div>
            <div className="gym-info">
            <h3 className="gym-name">{gym.name || 'Unnamed Gym'}</h3>
            <div className="gym-role">
            Role: Admin
            </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyGymsList;
